import {
  Alert,
  AppShell,
  Button,
  Card,
  Center,
  Flex,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useDeleteClubMutation } from '@/features/admin-panel/api/deleteClub/hook';
import { useGetClubsActivityListQuery } from '@/features/admin-panel/api/getClubsActivitiList/hook';
import { useCreateConversationMutation } from '@/features/chat/api';
import { useChatStore } from '@/features/chat/store/chatStore';
import { ClubFullProfile } from '@/features/work/view/club-full-profile/ClubFullProfile';

export const AdminClubsList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useGetClubsActivityListQuery({
    pageNumber: page.toString()
  });

  const totalCount =
    data?.data.totalCount ??
    (data?.data.totalPages ?? 0) * (data?.data.pageSize ?? 10);
  const { chatProfile } = useChatStore();
  const createConversation = useCreateConversationMutation();
  const deleteClub = useDeleteClubMutation();

  const [isProfileOpen, { open: openProfile, close: closeProfile }] =
    useDisclosure();
  const [currentClubId, setCurrentClubId] = useState<string | null>(null);

  const [isDeleteOpen, { open: openDelete, close: closeDelete }] =
    useDisclosure();
  const [deleteClubId, setDeleteClubId] = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');

  const handleConversation = async (senderId: string, recipientId: string) => {
    const response = await createConversation.mutateAsync({
      params: {
        senderProfileId: senderId,
        recipientProfileId: recipientId,
        isDirect: true
      }
    });
    const convId = response.data.conversationId.toString();
    navigate({
      to: '/admin/chat/conversation/$conversationId',
      params: { conversationId: convId }
    });
  };

  const onDeleteClick = (clubId: string) => {
    setDeleteClubId(clubId);
    setConfirmInput('');
    openDelete();
  };

  const confirmDelete = async () => {
    if (!deleteClubId) {
      return;
    }
    await deleteClub.mutateAsync({ profileId: deleteClubId });
    closeDelete();
    refetch();
  };

  return (
    <AppShell.Main>
      {/* Club Profile Modal */}
      <Modal
        withCloseButton
        opened={isProfileOpen}
        overlayProps={{ blur: 4, opacity: 0.6 }}
        onClose={closeProfile}
      >
        {currentClubId && (
          <ScrollArea p='md'>
            <ClubFullProfile clubId={currentClubId} />
          </ScrollArea>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        withCloseButton
        opened={isDeleteOpen}
        overlayProps={{ blur: 4, opacity: 0.6 }}
        title='Удаление клуба'
        onClose={closeDelete}
      >
        <Text mb='sm'>
          Вы уверены, что хотите удалить этот клуб? Действие необратимо. Введите
          &quot;delete&quot; для подтверждения.
        </Text>
        <TextInput
          placeholder='Введите delete для подтверждения'
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.currentTarget.value)}
        />
        <Flex gap='xs' justify='end' mt='md'>
          <Button variant='default' onClick={closeDelete}>
            Отмена
          </Button>
          <Button
            color='red'
            disabled={confirmInput !== 'delete'}
            onClick={confirmDelete}
          >
            Удалить
          </Button>
        </Flex>
      </Modal>

      {/* Header */}
      <Flex align='center' justify='space-between' mb='lg'>
        <Title order={2}>Клубы</Title>
      </Flex>

      {/* States: Loading, Error, Empty */}
      {isLoading && (
        <Center style={{ height: 200 }}>
          <Loader />
        </Center>
      )}
      {isError && (
        <Alert color='red' mb='md' title='Ошибка'>
          Не удалось загрузить список клубов.
        </Alert>
      )}
      {!isLoading && data?.data.items.length === 0 && (
        <Center>
          <Text color='dimmed'>Нет клубов для отображения.</Text>
        </Center>
      )}

      {/* Clubs Table */}
      {!isLoading && data && (
        <Card withBorder p='lg' radius='md' shadow='sm'>
          <ScrollArea>
            <Table highlightOnHover striped withColumnBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>No</Table.Th>
                  <Table.Th>Название</Table.Th>
                  <Table.Th>Телефон</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Страна</Table.Th>
                  <Table.Th>Адрес</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.data.items.map((club, index) => (
                  <Table.Tr key={club.clubProfileId}>
                    <Table.Td>
                      {totalCount -
                        ((page - 1) * (data.data.pageSize ?? 10) + index)}
                    </Table.Td>
                    <Table.Td>{club.name ?? '-'}</Table.Td>
                    <Table.Td>{club.phone ?? '-'}</Table.Td>
                    <Table.Td>{club.email ?? '-'}</Table.Td>
                    <Table.Td>{club.country ?? '-'}</Table.Td>
                    <Table.Td
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: 200
                      }}
                    >
                      {club.address ?? '-'}
                    </Table.Td>
                    <Table.Td>
                      <Flex gap='xs'>
                        <Button
                          size='xs'
                          variant='outline'
                          onClick={() => {
                            setCurrentClubId(club.clubProfileId.toString());
                            openProfile();
                          }}
                        >
                          Профиль
                        </Button>
                        <Button
                          size='xs'
                          variant='filled'
                          onClick={() =>
                            handleConversation(
                              chatProfile?.chatProfileId.toString()!,
                              club.chatProfileId.toString()
                            )
                          }
                        >
                          Чат
                        </Button>
                        <Button
                          color='red'
                          size='xs'
                          variant='outline'
                          onClick={() =>
                            onDeleteClick(club.clubProfileId.toString())
                          }
                        >
                          Удалить
                        </Button>
                      </Flex>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Flex justify='center' mt='md'>
            <Pagination
              boundaries={1}
              siblings={1}
              total={data.data.totalPages}
              value={page}
              onChange={setPage}
            />
          </Flex>
        </Card>
      )}
    </AppShell.Main>
  );
};
